// ============================================
// 视觉绑定应用 Hook（阶段5+7+8）：绑定规则 → 设备点位实时值 → 应用/恢复
// 支持 FBX 子对象定位（阶段8 补漏）：ruleConfig.subName → 只操作该命名构件的 Mesh
//   color  → 区间求值 → 写材质 color
//   height → 预留（阶段7 LevelTank 已由 DeviceAnimationRig 处理）
// 挂载：SceneEditor / DashboardEditor（联动预览同源）
// ============================================

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSceneBindingStore } from '../stores/sceneBindingStore';
import { useDeviceDataStore } from '../stores/deviceDataStore';
import { modelRefsRegistry } from '../stores/modelRefsRegistry';
import { resolveVisualRule } from '../core/visualRule';

/** nodeId:visualType → key → 原始颜色 Map（Mesh uuid:matIndex → Color） */
const originalColors = new Map<string, Map<string, THREE.Color>>();

/** 按 subName / subObjectUuid 筛选目标 Mesh 集合（都不传 → 整个 group 的全部 Mesh）
 *  2026-09-08｜阶段4：优先 subObjectUuid（uuid 稳定定位组合模型内部构件），兼容旧 subName 按名定位 */
function getTargetMeshes(group: THREE.Group | null, subName?: string, subObjectUuid?: string): THREE.Mesh[] {
  if (!group) return [];
  if (subObjectUuid) {
    const target = group.getObjectByProperty('uuid', subObjectUuid);
    if (!target) return [];
    const meshes: THREE.Mesh[] = [];
    target.traverse((c) => { if ((c as any).isMesh) meshes.push(c as THREE.Mesh); });
    if ((target as any).isMesh) meshes.unshift(target as THREE.Mesh);
    return meshes;
  }
  const meshes: THREE.Mesh[] = [];
  if (subName) {
    group.traverse((child) => {
      if (child.name === subName) {
        child.traverse((c) => { if ((c as any).isMesh) meshes.push(c as THREE.Mesh); });
        if ((child as any).isMesh) meshes.push(child as THREE.Mesh);
      }
    });
  } else {
    group.traverse((child) => {
      if ((child as any).isMesh) meshes.push(child as THREE.Mesh);
    });
  }
  return meshes;
}

function recordOriginals(meshes: THREE.Mesh[], key: string) {
  if (!meshes.length || originalColors.has(key)) return;
  const map = new Map<string, THREE.Color>();
  meshes.forEach((mesh, i) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat, j) => {
      if ('color' in mat) map.set(`${mesh.uuid}:${i}:${j}`, (mat as any).color.clone());
    });
  });
  originalColors.set(key, map);
}

function restoreOriginals(meshes: THREE.Mesh[], key: string) {
  const map = originalColors.get(key);
  if (!map) return;
  meshes.forEach((mesh) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat, j) => {
      const orig = map.get(`${mesh.uuid}:0:${j}`);
      if (orig && 'color' in mat) { (mat as any).color.copy(orig); mat.needsUpdate = true; }
    });
  });
  originalColors.delete(key);
}

function paintColor(meshes: THREE.Mesh[], color: string) {
  meshes.forEach((mesh) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat) => {
      if ('color' in mat) { (mat as any).color?.setStyle(color); mat.needsUpdate = true; }
    });
  });
}

export function useVisualBindingApply() {
  const appliedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const evaluate = () => {
      const bindings = useSceneBindingStore.getState().bindings;
      const pointValue = (deviceId?: string, key?: string) =>
        deviceId && key ? useDeviceDataStore.getState().deviceMap[deviceId]?.points?.[key] : undefined;

      const active = new Set<string>();
      for (const b of bindings) {
        const key = `${b.modelNodeId}:${b.visualType}`;
        active.add(key);
        const handle = modelRefsRegistry.get(b.modelNodeId);
        if (!handle?.group) continue;
        const value = pointValue(b.deviceId, b.bindPointKey);
        if (b.visualType === 'color') {
          const color = resolveVisualRule(value, b.ruleConfig);
          const subName = (b.ruleConfig as any)?.subName as string | undefined;
          const subObjectUuid = (b.ruleConfig as any)?.subObjectUuid as string | undefined;
          const meshes = getTargetMeshes(handle.group, subName, subObjectUuid);
          if (color) {
            recordOriginals(meshes, key);
            paintColor(meshes, color);
            appliedRef.current.add(key);
          } else if (appliedRef.current.has(key)) {
            restoreOriginals(meshes, key);
            appliedRef.current.delete(key);
          }
        }
      }
      for (const k of appliedRef.current) {
        if (!active.has(k)) {
          const [nodeId] = k.split(':');
          const handle = modelRefsRegistry.get(nodeId);
          const meshes = getTargetMeshes(handle?.group ?? null);
          restoreOriginals(meshes, k);
        }
      }
      appliedRef.current = new Set(Array.from(appliedRef.current).filter((k) => active.has(k)));
    };

    const unsubs = [
      useSceneBindingStore.subscribe(() => evaluate()),
      useDeviceDataStore.subscribe(() => evaluate()),
    ];
    evaluate();
    return () => {
      unsubs.forEach((u) => u());
      for (const k of appliedRef.current) {
        const [nodeId] = k.split(':');
        const handle = modelRefsRegistry.get(nodeId);
        restoreOriginals(getTargetMeshes(handle?.group ?? null), k);
      }
      appliedRef.current.clear();
    };
  }, []);
}